import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import { Navbar, NavBody, NavItems, MobileNav, NavbarLogo, NavbarButton, MobileNavHeader, MobileNavToggle, MobileNavMenu } from "@/components/ui/resizable-navbar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import companyLogo from "@/assets/ulture-logo-transparent.png";
const languages = [{
  code: "en",
  label: "English",
  flag: "🇬🇧"
}, {
  code: "es",
  label: "Español",
  flag: "🇪🇸"
}, {
  code: "ar",
  label: "العربية",
  flag: "🇸🇦"
}, {
  code: "it",
  label: "Italiano",
  flag: "🇮🇹"
}];
export function LandingHeader() {
  const {
    t,
    i18n
  } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];
  const navigate = useNavigate();
  const location = useLocation();
  const navItems = [{
    name: t("landing.nav.home", "Home"),
    link: "/"
  }, {
    name: t("landing.nav.pricing", "Pricing"),
    link: "/#pricing"
  }, {
    name: t("landing.nav.blog", "Blog"),
    link: "/blog"
  }, {
    name: t("landing.nav.contact", "Contact"),
    link: "/contact"
  }];
  const handleLanguageChange = async (langCode: string) => {
    // Preserve scroll position across language change to avoid jumping
    // back to the hero when locale resources re-render and lazy sections
    // re-suspend. We re-apply scrollTo every frame for ~700ms to survive
    // any late re-layout.
    const x = window.scrollX;
    const y = window.scrollY;
    const html = document.documentElement;
    const prevBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";

    await i18n.changeLanguage(langCode);

    const start = performance.now();
    const restore = (now: number) => {
      window.scrollTo({ top: y, left: x, behavior: "auto" });
      if (now - start < 700) {
        requestAnimationFrame(restore);
      } else {
        html.style.scrollBehavior = prevBehavior;
      }
    };
    requestAnimationFrame(restore);
  };


  const scrollToAnchor = (hash: string, attempt = 0) => {
    const el = document.getElementById(hash);
    if (el) {
      const headerOffset = window.matchMedia("(max-width: 1023px)").matches ? 92 : 112;
      const top = Math.max(0, el.getBoundingClientRect().top + window.scrollY - headerOffset);
      window.history.replaceState(null, "", `/#${hash}`);
      window.scrollTo({ top, behavior: "smooth" });
      return;
    }

    if (attempt < 20) {
      window.requestAnimationFrame(() => scrollToAnchor(hash, attempt + 1));
    }
  };

  const handleNavigation = (link: string) => {
    setIsMobileMenuOpen(false);
    if (link.startsWith("/#")) {
      const hash = link.slice(2);
      if (location.pathname === "/") {
        window.requestAnimationFrame(() => scrollToAnchor(hash));
      } else {
        navigate(`/#${hash}`);
        window.requestAnimationFrame(() => scrollToAnchor(hash));
      }
      return;
    }
    navigate(link);
  };
  const isActive = (href: string) => {
    return location.pathname === href;
  };
  return <Navbar>
      {/* Desktop Navigation */}
      <NavBody>
        <NavbarLogo onClick={() => handleNavigation("/")}>
          <img src={companyLogo} alt="Ulture Pest" width={32} height={32} className="h-8 w-8 object-contain" />
          <span className="font-heading font-bold">Ulture Pest </span>
        </NavbarLogo>

        <NavItems items={navItems} onItemClick={handleNavigation} />

        <div className="relative z-20 flex items-center gap-2">
          {/* Language Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-9 gap-2 rounded-full bg-transparent px-3 text-white/70 hover:bg-white/10 hover:text-white">
                <Globe className="h-4 w-4" />
                <span className="text-sm">{currentLang.flag}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="z-[100] border-white/10 bg-[hsl(220,25%,12%)] text-white">
              {languages.map(lang => <DropdownMenuItem key={lang.code} onClick={() => handleLanguageChange(lang.code)} className={cn("cursor-pointer gap-2 hover:bg-white/10 focus:bg-white/10", currentLang.code === lang.code && "bg-white/5")}>
                  <span>{lang.flag}</span>
                  <span>{lang.label}</span>
                </DropdownMenuItem>)}
            </DropdownMenuContent>
          </DropdownMenu>


          <NavbarButton variant="secondary" onClick={() => handleNavigation("/auth")}>
            {t("landing.nav.signIn", "Sign In")}
          </NavbarButton>

          <NavbarButton variant="cta" onClick={() => handleNavigation("/#pricing")}>
            {t("landing.nav.getStarted", "Get Started")}
          </NavbarButton>
        </div>
      </NavBody>

      {/* Mobile Navigation */}
      <MobileNav>
        <MobileNavHeader>
          <NavbarLogo onClick={() => handleNavigation("/")}>
            <img src={companyLogo} alt="Ulture Pest" width={28} height={28} className="h-7 w-7 object-contain" />
            <span className="text-base font-heading font-bold">Ulture Pest </span>
          </NavbarLogo>
          <MobileNavToggle isOpen={isMobileMenuOpen} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} />
        </MobileNavHeader>

        <MobileNavMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)}>
          {navItems.map((item, idx) => <button key={`mobile-nav-${idx}`} onClick={() => handleNavigation(item.link)} className={cn("w-full rounded-lg px-4 py-3 text-left text-sm font-medium transition-colors", isActive(item.link) ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5 hover:text-white")}>
              {item.name}
            </button>)}

          <div className="my-2 h-px bg-white/10" />

          {/* Mobile Language Selector */}
          <div className="flex items-center gap-2 px-4 py-2">
            <Globe className="h-4 w-4 text-white/60" />
            <span className="text-sm text-white/60">
              {t("landing.nav.language", "Language")}:
            </span>
            <div className="flex gap-1">
              {languages.map(lang => <button key={lang.code} onClick={() => handleLanguageChange(lang.code)} className={cn("rounded-md px-2 py-1 text-lg transition-colors", currentLang.code === lang.code ? "bg-white/10" : "hover:bg-white/5")}>
                  {lang.flag}
                </button>)}
            </div>
          </div>

          <div className="my-2 h-px bg-white/10" />

          <div className="flex flex-col gap-2 pt-2">
            <NavbarButton variant="secondary" onClick={() => handleNavigation("/auth")} className="w-full justify-center">
              {t("landing.nav.signIn", "Sign In")}
            </NavbarButton>
            <NavbarButton variant="cta" onClick={() => handleNavigation("/#pricing")} className="w-full justify-center">
              {t("landing.nav.getStarted", "Get Started")}
            </NavbarButton>
          </div>
        </MobileNavMenu>
      </MobileNav>
    </Navbar>;
}